import { AppDataContext } from "modules/main/context/appData";
import { useCart } from "modules/cart/hooks/useCart";
import { useFetch } from "modules/main/hooks/useFetch";
import { useCallback, useContext, useEffect, useState} from "react";
import { endPointCheckStatus, endPointOptimized, endPointOptimizedResults } from "modules/main/services/endpoints";
import { OptimizedCartResponse, OptimizedCartRequest, emptyScenario, OptimizedCartSentResponse, OptimizedCartStatusResponse, CartCreationStatus, CartItemStatus } from "modules/checkout/types/optimizedCart";
import { FetchDataOptions} from "modules/main/services/fetchData";

import { CartTableDetail } from "modules/checkout/components/cartDetailTable";
import { CartTableResume } from "modules/checkout/components/cartResumeTable";
import Header from "modules/main/components/header";
import { AuthError } from "modules/authentication/types/form-login-types";
import { NoAuthError } from "modules/authentication/components/no-auth-error";
import { GeneralError } from "modules/main/components/general-error";
import { isDesktopSize } from "utils/is-desktop";
import { useMediaQuery } from "@/hooks/use-media-query";
import { PlanRequiredError } from "modules/main/services/errors";
import { PlanRequiredErrorView } from "modules/checkout/components/plan-required-error-view";


import { WaitingCalc } from "../components/waitingCalc";
import { CartItem, Payload } from "modules/cart/types/cart";
import { addDeletefromCartOptimized, compareCartContents } from "utils/cartUtils";
import { useOptimizedCartUpdates } from "../hooks/useOptimizedScenarioUpdate";
import { getTotalsFromScenario } from "utils/scenarioUtils";

import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from "@/components/ui/empty"
import { ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, Navigate } from "react-router-dom";
import { ScenarioResults } from "../components/ScenarioResults";
import { getVisitorId } from "utils/analytics";
import { useAuth } from "modules/authentication/hooks/use-auth";
import { usePageTitle } from "hooks/usePageTitle";




export const Optimizing = () => {     
    const {cart} = useCart();       
    const { setTitle, setShowCart} = usePageTitle();
    const [isCalculating, setIsCalculating] = useState(true);
    const {isLogged} = useAuth(); 
    const [resultsOK, setResultsOK] = useState<boolean>(false);
    const [taskError, setTaskError] = useState<any>(null);
    const [orderId, setOrderId] = useState<number | null>(null);

    const userDecisions = cart
        .filter((item) => item.productDecision != null)
        .map((item) => item.productDecision!);

    const bodyRequest: OptimizedCartRequest = {
        products: cart,
        user_decisions: userDecisions.length > 0 ? userDecisions : undefined,
        // preferred_stores: storeList.map(store => store.id)
    }
    // console.log("Decisions received in OptimizedCart:", decisions);
    const requestOptions:FetchDataOptions = {
        method:"POST",
        headers:{
            "content-type":"application/json",
            ...(!isLogged ? { "x-visitor-id": getVisitorId() } : {})
        },
        body:bodyRequest
    }
    
    const {data,error,loading} = useFetch<OptimizedCartSentResponse>(endPointOptimized(),requestOptions);
    useEffect(()=>{
      setTitle("");
      setShowCart(false);
    },[setTitle]);  


    useEffect(()=>{
        if(!data || loading)
            return;

        if(data?.order_id){
            setOrderId(data.order_id);
        }

        // if((error || !orderId)&&!loading){
        //     setIsCalculating(false);
        //     return;
        // }

        const checkOrderStatus = async () => {
            try {
                const response = await fetch(endPointCheckStatus(orderId?.toString()??""),{
                    method: "GET",
                    credentials:"include",
                    headers: {
                        "content-type":"application/json",
                        ...(!isLogged ? { "x-visitor-id": getVisitorId() } : {})
                    }
                })
                const result = await response.json() as OptimizedCartStatusResponse;
                if(result.status === "AC"){
                    setResultsOK(true);
                    setIsCalculating(false);
                    return true;
                }
                if(result.status === "CN"){
                    setTaskError(result.error??"Error desconocido");
                    setIsCalculating(false);
                    return true;
                }
                return false;
            }
            catch(e){
                setTaskError(e);
                setIsCalculating(false);
                return true;
            }            
        }

        const interval = setInterval(async () => {
            const shouldStop = await checkOrderStatus();
            if(shouldStop) {
                clearInterval(interval);
            }
        },3000);

        return () => clearInterval(interval);

    },[data?.order_id,orderId])
    


    if(error){
        if (error instanceof AuthError) {
            return (<><NoAuthError/></>);
        }
        if (error instanceof PlanRequiredError) {
            return (<><PlanRequiredErrorView reason={error.body.reason ?? "monthly_optimization_limit"} quota={error.body.quota} /></>);
        }
        return (<><GeneralError/></>);
    }

    if(isCalculating){
        return (<>
        <main className="flex flex-row  w-full  gap-20 py-4 px-6  lg:p-8 flex-wrap">
            <WaitingCalc isPreCheckout={false} />
        </main>
        </>);
    }
    
    if(!orderId){
        return (<><GeneralError/></>);
    }

    return (
        <Navigate to={`/cart/results/${orderId}`}/>
    );
}


interface OptimizedCartPageProps{
    orderId:number,    
}


// TODO: esto contiene la logica de edici'on del carrito, se debe terminar de refactorizar al componente ScenarioResults
const OptimizedCartPage:React.FC<OptimizedCartPageProps> = ({orderId}) => {
    const {cart, clearCart} = useCart();       
    // const [recalculate, setRecalculate] = useState(false);    
    const {storeList} = useContext(AppDataContext); 
    const isDesktop = useMediaQuery(isDesktopSize);
    // const [refresh, setRefresh] = useState(false);

    const [workingCart, setWorkingCart] = useState<CartItem[]>([]);    
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);



    const [cartStatus, setCartStatus] = useState<CartCreationStatus[]>([]);

    

    const [scenarios, setScenarios] = useState<OptimizedCartResponse["scenarios"]>([]);

    const [selectedIndexScenario, setSelectedIndexScenario] = useState(0);
    
    const { applyChanges: applyCartChanges, isApplyingChanges } = useOptimizedCartUpdates({
            orderId,
            selectedIndexScenario,
            onSuccess: (updatedData) => {
                setHasUnsavedChanges(false);
                // Actualizar workingCart con datos del servidor
                const serverCart = updatedData.scenarios[selectedIndexScenario].opt_cart.map(item => ({
                    ...item,
                    price_component: item.price_component.map(pc => ({...pc}))
                }));
                setWorkingCart(serverCart);
                setScenarios(updatedData.scenarios);
            },
            onError: (error) => {
                console.warn("Error aplicando cambios:", error.message);
                setHasUnsavedChanges(true);
            }
    });

    const applyChanges = () => applyCartChanges(scenarios[selectedIndexScenario].order_id??0, workingCart);
    
    const initializeWorkingArea = (scenarioIndex:number,scenariosData?: OptimizedCartResponse["scenarios"]) =>{
        const currentScenarios = scenariosData || scenarios;
        //Clone cart 
        if (currentScenarios && currentScenarios[scenarioIndex]) {
            const clonedCart = currentScenarios[scenarioIndex].opt_cart.map(item => ({
                ...item,
                price_component: item.price_component.map(pc => ({...pc}))
            }));
            setWorkingCart(clonedCart);
            setHasUnsavedChanges(false);
        }
    }

    const initializeCartStatus = useCallback((scenarioIndex: number, scenariosData?: OptimizedCartResponse["scenarios"]) => {
        const currentScenarios = scenariosData || scenarios;
        if (!currentScenarios || !currentScenarios[scenarioIndex]) return;

        const currentScenario = currentScenarios[scenarioIndex];
        const isUniqueStore = currentScenario.scenarioType === "UNIQUE_STORE";
        const { cheapestStoreId } = getTotalsFromScenario(currentScenario, isUniqueStore, false);

        const newCartStatus: CartCreationStatus[] = currentScenario.store_resume
                .filter(store=>((cheapestStoreId === store.id && isUniqueStore) || !isUniqueStore))
                .map(store => {
                    const productsForStore: CartItemStatus[] = currentScenario.opt_cart.reduce((acc, item) => {
                        const storeComponent = item.price_component.find(pc => pc.id === store.id && !pc.no_met_conditions);
                        if (storeComponent && storeComponent.quantity > 0) {
                            acc.push({
                                ...item,
                                price_component: item.price_component.map(pc => ({
                                    ...pc,
                                    checked: false
                                }))
                            });
                        }
                        return acc;
                    }, [] as CartItemStatus[]);

                    return {
                        store_id: store.id,
                        products: productsForStore
                    };
                });
                setCartStatus(newCartStatus);
    }, [scenarios]);

    // useEffect(() => {
    // if (data?.scenarios && data.scenarios.length > 0) {
    //     // Solo inicializar una vez al cargar los datos
    //     setScenarios(data.scenarios);
    //     const reccomendedIndex = Math.max(0, scenarios.findIndex(scenario => scenario.reccomended) ?? 0);
    //     setSelectedIndexScenario(reccomendedIndex);
    //     if(workingCart.length === 0){
    //         initializeWorkingArea(reccomendedIndex, data.scenarios);
    //         initializeCartStatus(reccomendedIndex, data.scenarios);
    //     }
        
    // }
    // }, [data, initializeCartStatus]);

    const handleScenarioChange = useCallback((index: number, loading: boolean) => {
        
        if (!scenarios?.[index] || loading) return;
        
        const clonedCart = scenarios[index].opt_cart.map(item => ({
            ...item,
            price_component: item.price_component.map(pc => ({...pc}))
        }));
        setSelectedIndexScenario(index);
        setWorkingCart(clonedCart);
        setHasUnsavedChanges(false);
        initializeCartStatus(index);
    }, [scenarios, initializeCartStatus]);

    
    // Función para descartar cambios
    const resetWorkingCart = () => {
        initializeWorkingArea(selectedIndexScenario);
    };



    // if(error){
    //     const ErrorComponent = (error instanceof AuthError) ? NoAuthError : GeneralError;
    //     return (<><ErrorComponent/></>);
    // }

    // if(!data){
    //     return (<>Loading</>);
    // }
    
    if(scenarios.length === 0){
        return (
        <div className="w-full flex justify-center items-center min-h-[50dvh]">
            <Empty>
                <EmptyHeader>
                    <EmptyMedia variant="icon">
                    <ShoppingCart />
                    </EmptyMedia>
                    <EmptyTitle>Tu carrito aún es muy pequeño</EmptyTitle>
                    <EmptyDescription>
                    Todavía no alcanzas el monto mínimo para optimizar precios entre tiendas.
                    Agrega más productos y podrás ver cómo se distribuye tu compra para ahorrar.
                    </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                    <div className="flex gap-2">
                    <Link to="/">
                        <Button>Agregar más productos</Button>
                    </Link>                
                    </div>
                </EmptyContent>
            </Empty>
        </div>
    );
    }
    
    return (
    <>    

    </>
    );
}
